import React from 'react';
import { Box, Typography, Card, CardContent, Button, Avatar, Chip, Switch, FormControlLabel, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, Tabs, Tab } from '@mui/material';
import { ShoppingCart as ShoppingCartIcon, Work as WorkIcon, LocalHospital as LocalHospitalIcon, School as SchoolIcon, CheckCircle as CheckCircleIcon, PriceCheck as FixedIcon, Inventory2 as MultipleIcon, Handshake as NegotiableIcon, Gavel as AuctionIcon, LocalOffer as PromotionIcon, Person as PersonIcon, Apps as AppsIcon, AlternateEmail as AlternateEmailIcon, Link as LinkIcon, HomeWork as RealEstateIcon, DesignServices as ServicesIcon, LocalShipping as TransportIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import toast from 'react-hot-toast';
import { useAppMode } from '../../contexts/AppModeContext';
import { AdminKeyService } from '../../services/adminKeyService';
import { uploadFileToBackblaze, presignB2, uploadToB2 } from '../../services/backblazeService';
import { supabase } from '../../lib/supabase';
import BackblazeHowToDialog from './BackblazeHowToDialog';
import { useEffect, useState } from 'react';
import { colorSchemes, getColorSchemeById } from '../../theme/colorSchemes';

const modes = [
  { id: 'shop', label: 'Shop', description: 'Sell products and manage orders', icon: <ShoppingCartIcon />, color: 'primary' },
  { id: 'job', label: 'Job Portal', description: 'Post jobs and manage applications', icon: <WorkIcon />, color: 'secondary' },
  { id: 'med', label: 'Medical Platform', description: 'Manage medical services and bookings', icon: <LocalHospitalIcon />, color: 'error' },
  { id: 'edu', label: 'Education Platform', description: 'Courses, lessons and enrollments', icon: <SchoolIcon />, color: 'success' },
  { id: 'real_estate', label: 'Real Estate', description: 'Listings, agents and property lead management', icon: <RealEstateIcon />, color: 'info', comingSoon: true },
  { id: 'services', label: 'Services', description: 'Bookings, scheduling and service marketplace', icon: <ServicesIcon />, color: 'warning', comingSoon: true },
  { id: 'transport', label: 'Drives / Transport', description: 'Rides, deliveries and fleet operations', icon: <TransportIcon />, color: 'info', comingSoon: true },
] as const;

const WebsiteSettingsTab: React.FC = () => {
  const theme = useTheme();
  const { mode, setMode } = useAppMode();
  const [secret, setSecret] = useState<string | null>(null);
  const [loadingSecret, setLoadingSecret] = useState<boolean>(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoFontFile, setLogoFontFile] = useState<File | null>(null);
  const [logoPath, setLogoPath] = useState<string | null>(null);
  const [logoFontPath, setLogoFontPath] = useState<string | null>(null);
  const [savingLogos, setSavingLogos] = useState<boolean>(false);
  // Media gallery support for selecting branding images
  const [gallery, setGallery] = useState<string[]>([]);
  const [galleryPicker, setGalleryPicker] = useState<{ open: boolean; target: 'logo' | 'logoFont' | null; selected: string | null }>({ open: false, target: null, selected: null });
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState<string>('');
  const [featureBtnText, setFeatureBtnText] = useState<string>('');
  const [platformName, setPlatformName] = useState<string>('');
  const [mainAuthorName, setMainAuthorName] = useState<string>('');
  const [supportEmail, setSupportEmail] = useState<string>('');
  const [appUrl, setAppUrl] = useState<string>('');
  const [savingIdentity, setSavingIdentity] = useState<boolean>(false);
  // Layout settings
  const [layoutContainer, setLayoutContainer] = useState<string>('lg');
  const [layoutPaddingX, setLayoutPaddingX] = useState<number>(0);
  const [layoutPaddingMode, setLayoutPaddingMode] = useState<'preset' | 'custom'>('preset');
  const [layoutPaddingPreset, setLayoutPaddingPreset] = useState<number>(16);
  const [layoutEditTab, setLayoutEditTab] = useState<'width' | 'padding'>('width');
  const [savingLayout, setSavingLayout] = useState<boolean>(false);
  const isShopMode = mode === 'shop';
  const [colorScheme, setColorScheme] = useState<string>('sky');
  const [savingColorScheme, setSavingColorScheme] = useState<boolean>(false);
  const [productOptions, setProductOptions] = useState<{[k: string]: boolean}>({
    fixed_single: false,
    fixed_multiple: false,
    negotiable_single: false,
    negotiable_multiple: false,
    auction: false,
    promotion: false,
  });
  const [backblaze, setBackblaze] = useState<{ enabled: boolean; bucket: string; endpoint: string; folderPrefix: string }>({
    enabled: false,
    bucket: '',
    endpoint: '',
    folderPrefix: 'uploads/',
  });
  const [savingBackblaze, setSavingBackblaze] = useState<boolean>(false);
  const [b2TestFile, setB2TestFile] = useState<File | null>(null);
  const [howToOpen, setHowToOpen] = useState<boolean>(false);
  const [b2KeyId, setB2KeyId] = useState<string>('');
  const [b2ApplicationKey, setB2ApplicationKey] = useState<string>('');
  const [savingB2Secrets, setSavingB2Secrets] = useState<boolean>(false);
  const [b2SecretsPresent, setB2SecretsPresent] = useState<boolean | null>(null);
  const [b2RowId, setB2RowId] = useState<string | null>(null);
  const [b2Items, setB2Items] = useState<Array<{ key: string; size: number; lastModified: string; url?: string }>>([]);
  const [b2NextToken, setB2NextToken] = useState<string | null>(null);
  const [b2PublicBase, setB2PublicBase] = useState<string | null>(null);
  const [b2LoadingList, setB2LoadingList] = useState<boolean>(false);
  const [b2Preview, setB2Preview] = useState<{ open: boolean; url: string | null; name: string | null }>({ open: false, url: null, name: null });
  const [uploadTarget, setUploadTarget] = useState<'supabase' | 'backblaze'>('supabase');
  const [savingUploadTarget, setSavingUploadTarget] = useState<boolean>(false);

  const loadSecret = async () => {
    try {
      setLoadingSecret(true);
      const s = await AdminKeyService.getSecret();
      setSecret(s);
      if (!s) {
        toast.error('Unable to load admin key from Supabase');
      }
    } finally {
      setLoadingSecret(false);
    }
  };

  const saveColorScheme = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }

    const loadingId = toast.loading('Saving color scheme...');
    setSavingColorScheme(true);
    try {
      const payload: any = { mode, colorScheme, token };
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }
      // Live update without full reload
      try {
        window.dispatchEvent(new CustomEvent('websiteSettingsUpdated', { detail: { colorScheme } }));
      } catch {
        // ignore
      }
      toast.success('Color scheme saved', { id: loadingId });
    } catch (e: any) {
      toast.error(`Failed to save color scheme: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setSavingColorScheme(false);
    }
  };

  // Dev helper to resolve /uploads paths to PHP server on port 8000
  const toPublicUrl = (p?: string | null) => {
    if (!p) return '';
    if (/^https?:\/\//i.test(p)) return p;
    try {
      const port = window.location.port;
      const isDevDifferentOrigin = port && port !== '8000';
      if (isDevDifferentOrigin && p.startsWith('/')) {
        const base = `${window.location.protocol}//${window.location.hostname}:8000`;
        return base + p;
      }
    } catch {}
    return p;
  };

  const saveLayout = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret; if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key. Please try again or contact support.'); return; }
    setSavingLayout(true);
    const loadingId = toast.loading('Layout wird gespeichert...');
    try {
      const effectivePx = layoutPaddingMode === 'preset' ? layoutPaddingPreset : layoutPaddingX;
      const pxClamped = Math.max(0, Math.min(200, Number(effectivePx) || 0));
      const payload: any = { mode, layout: { container: layoutContainer, contentPaddingX: pxClamped, paddingMode: layoutPaddingMode, paddingPreset: layoutPaddingPreset }, token };
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
      const raw = await res.text(); let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) { const detail = json?.error || raw || 'No response body'; throw new Error(`${res.status} ${res.statusText} - ${detail}`); }
      // Reflect saved values from server response if present
      const ly = json?.layout;
      if (ly) {
        if (typeof ly.container === 'string') setLayoutContainer(ly.container);
        if (typeof ly.contentPaddingX === 'number') setLayoutPaddingX(ly.contentPaddingX);
        if (typeof ly.paddingMode === 'string') setLayoutPaddingMode(ly.paddingMode === 'custom' ? 'custom' : 'preset');
        if (typeof ly.paddingPreset === 'number') setLayoutPaddingPreset(ly.paddingPreset);
      }
      toast.success('Layout gespeichert', { id: loadingId });
    } catch (e: any) {
      toast.error(`Speichern fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setSavingLayout(false);
    }
  };

  const saveUploadTarget = async (next: 'supabase' | 'backblaze') => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }
    setSavingUploadTarget(true);
    const loadingId = toast.loading('Saving upload target...');
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ mode, uploadTarget: next, token }),
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }
      setUploadTarget(next);
      toast.success(`Upload target: ${next === 'backblaze' ? 'Backblaze' : 'Supabase'}`, { id: loadingId });
    } catch (e: any) {
      toast.error(`Failed to save: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setSavingUploadTarget(false);
    }
  };

  const fetchBackblazeList = async (mode: 'reset' | 'next' = 'reset') => {
    if (mode === 'next' && !b2NextToken) return;
    const loadingId = toast.loading(mode === 'reset' ? 'Fetching files...' : 'Loading more...');
    setB2LoadingList(true);
    try {
      let token = secret;
      if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
      if (!token) throw new Error('Missing admin key');
      const payload: any = {
        bucket: (backblaze.bucket || '').trim(),
        endpoint: (backblaze.endpoint || '').trim(),
        folderPrefix: (backblaze.folderPrefix || '').trim(),
        keyId: (b2KeyId || '').trim(),
        applicationKey: (b2ApplicationKey || '').trim(),
        maxKeys: 24,
        signRead: true,
      };
      if (mode === 'next' && b2NextToken) payload.continuationToken = b2NextToken;
      const res = await fetch('/api/b2-list.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        const hint = res.status === 404 ? ' Endpoint likely not available in local development. Deploy to your hosting or run a local PHP with OpenSSL enabled.' : '';
        throw new Error(`${res.status} ${res.statusText} - ${detail}${hint}`);
      }
      setB2PublicBase(typeof json.publicBaseUrl === 'string' ? json.publicBaseUrl : null);
      setB2NextToken(json.nextContinuationToken || null);
      const items: Array<{ key: string; size: number; lastModified: string; url?: string }> = (json.items || []).map((it: any) => ({
        key: it.key,
        size: it.size,
        lastModified: it.lastModified,
        url: (typeof it.url === 'string' && it.url.length > 0)
          ? it.url
          : (json.publicBaseUrl ? `${json.publicBaseUrl.replace(/\/$/, '')}/${it.key}` : undefined),
      }));
      if (mode === 'reset') setB2Items(items); else setB2Items((prev) => [...prev, ...items]);
      toast.success(`Loaded ${items.length} file(s)`, { id: loadingId });
    } catch (e: any) {
      toast.error(`Failed to fetch files: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setB2LoadingList(false);
    }
  };

  const testB2Connection = async () => {
    const loadingId = toast.loading('Testing Backblaze connection...');
    try {
      // Ensure we have admin token
      let token = secret;
      if (!token) {
        const s = await AdminKeyService.getSecret();
        setSecret(s);
        token = s;
      }
      if (!token) {
        throw new Error('Missing admin key');
      }

      const endpoint = '/api/b2-test-connection.php';
      const payload: any = {
        bucket: (backblaze.bucket || '').trim(),
        endpoint: (backblaze.endpoint || '').trim(),
        folderPrefix: (backblaze.folderPrefix || '').trim(),
        keyId: (b2KeyId || '').trim(),
        applicationKey: (b2ApplicationKey || '').trim(),
      };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let json: any = null;
      try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        const hint = res.status === 404 ? ' Endpoint likely not available in local development. Deploy to your hosting or run a local PHP with OpenSSL enabled.' : '';
        throw new Error(`${res.status} ${res.statusText} - ${detail}${hint}`);
      }
      const pieces: string[] = [];
      if (json.authorized) pieces.push('Authorized');
      if (json.bucketVerified) pieces.push('Bucket OK');
      toast.success(`Backblaze OK: ${pieces.join(' · ') || 'Success'}`, { id: loadingId });
    } catch (e: any) {
      toast.error(`Test failed: ${e?.message || 'Unknown error'}`, { id: loadingId });
    }
  };

  const saveB2Secrets = async () => {
    const keyIdTrim = (b2KeyId || '').trim();
    const appKeyTrim = (b2ApplicationKey || '').trim();
    if (!keyIdTrim || !appKeyTrim) {
      toast.error('Please enter both Key ID and Application Key.');
      return;
    }
    const loadingId = toast.loading('Saving Backblaze credentials...');
    setSavingB2Secrets(true);
    try {
      const { data: existing, error: findErr } = await supabase
        .from('backblaze_config')
        .select('id')
        .eq('active', true)
        .maybeSingle();
      if (findErr) throw findErr;
      if (existing) {
        const { error: updErr } = await supabase
          .from('backblaze_config')
          .update({ key_id: keyIdTrim, application_key: appKeyTrim })
          .eq('id', existing.id);
        if (updErr) throw updErr;
      } else {
        // If no row yet, create minimal row with secrets (non-secret fields can be saved later)
        const { error: insErr } = await supabase
          .from('backblaze_config')
          .insert({ active: true, bucket: '', endpoint: '', folder_prefix: 'uploads/', key_id: keyIdTrim, application_key: appKeyTrim });
        if (insErr) throw insErr;
      }
      setB2SecretsPresent(true);
      toast.success('Backblaze credentials saved', { id: loadingId });
    } catch (e: any) {
      toast.error(`Failed to save credentials: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setSavingB2Secrets(false);
    }
  };

  const saveProductOptions = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) {
      const s = await AdminKeyService.getSecret();
      setSecret(s);
      token = s;
    }
    if (!token) {
      toast.error('Missing admin key. Please try again or contact support.');
      return;
    }
    const loadingId = toast.loading('Saving product options...');
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ mode, productOptions, token }),
      });
      const raw = await res.text();
      let json: any = null;
      try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }
      if (json.productOptions) setProductOptions((prev) => ({ ...prev, ...json.productOptions }));
      toast.success('Product options saved', { id: loadingId });
    } catch (e: any) {
      toast.error(`Failed to save product options: ${e?.message || 'Unknown error'}`, { id: loadingId });
    }
  };

  const addKeywordsFromInput = () => {
    const parts = keywordInput
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (parts.length === 0) return;
    const lower = new Set(keywords.map((k) => k.toLowerCase()))
    const merged: string[] = [...keywords];
    parts.forEach((p) => {
      if (!lower.has(p.toLowerCase())) {
        merged.push(p);
        lower.add(p.toLowerCase());
      }
    });
    setKeywords(merged);
    setKeywordInput('');
  };

  const removeKeyword = (k: string) => {
    setKeywords((prev) => prev.filter((x) => x !== k));
  };

  const saveKeywords = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) {
      const s = await AdminKeyService.getSecret();
      setSecret(s);
      token = s;
    }
    if (!token) {
      toast.error('Missing admin key. Please try again or contact support.');
      return;
    }
    const loadingId = toast.loading('Saving keyword filters...');
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ mode, keywords, token }),
      });
      const raw = await res.text();
      let json: any = null;
      try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }
      if (Array.isArray(json.keywords)) setKeywords(json.keywords);
      toast.success('Keyword filters saved', { id: loadingId });
    } catch (e: any) {
      toast.error(`Failed to save keywords: ${e?.message || 'Unknown error'}`, { id: loadingId });
    }
  };

  const saveLogos = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    // Ensure we have token
    let token = secret;
    if (!token) {
      const s = await AdminKeyService.getSecret();
      setSecret(s);
      token = s;
    }
    if (!token) {
      toast.error('Missing admin key. Please try again or contact support.');
      return;
    }
    const fd = new FormData();
    fd.append('mode', mode);
    fd.append('token', token);
    if (logoPath) fd.append('logoPath', logoPath);
    if (logoFontPath) fd.append('logoFontPath', logoFontPath);
    if (logoFile) fd.append('logo', logoFile);
    else if (logoPath && logoPath.startsWith('data:')) fd.append('logoDataUrl', logoPath);
    if (logoFontFile) fd.append('logoFont', logoFontFile);
    else if (logoFontPath && logoFontPath.startsWith('data:')) fd.append('logoFontDataUrl', logoFontPath);
    const loadingId = toast.loading('Uploading logos...');
    setSavingLogos(true);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: fd,
      });
      const raw = await res.text();
      let json: any = null;
      try { json = raw ? JSON.parse(raw) : null; } catch { /* ignore */ }
      if (!res.ok || !json?.ok) {
        // Fallback for local development (no PHP backend): persist to localStorage as DataURL
        const toDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = (e) => reject(e);
          reader.readAsDataURL(file);
        });
        const saved: { logo?: string; logoFont?: string } = {};
        if (logoFile) saved.logo = await toDataUrl(logoFile);
        if (logoFontFile) saved.logoFont = await toDataUrl(logoFontFile);
        if (saved.logo) window.localStorage.setItem('branding.logoDataUrl', saved.logo);
        if (saved.logoFont) window.localStorage.setItem('branding.logoFontDataUrl', saved.logoFont);
        if (saved.logo) setLogoPath(saved.logo);
        if (saved.logoFont) setLogoFontPath(saved.logoFont);
        setLogoFile(null);
        setLogoFontFile(null);
        toast.success('Logos stored locally for development. They will appear in the header.', { id: loadingId });
        return;
      }
      if (typeof json.logoPath === 'string') setLogoPath(json.logoPath);
      if (typeof json.logoFontPath === 'string') setLogoFontPath(json.logoFontPath);
      setLogoFile(null);
      setLogoFontFile(null);
      toast.success('Logos updated', { id: loadingId });
    } catch (e: any) {
      // As a final fallback, try localStorage if we have files
      try {
        const toDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = (err) => reject(err);
          reader.readAsDataURL(file);
        });
        let anySaved = false;
        if (logoFile) {
          const data = await toDataUrl(logoFile);
          window.localStorage.setItem('branding.logoDataUrl', data);
          setLogoPath(data);
          anySaved = true;
        }
        if (logoFontFile) {
          const data = await toDataUrl(logoFontFile);
          window.localStorage.setItem('branding.logoFontDataUrl', data);
          setLogoFontPath(data);
          anySaved = true;
        }
        setLogoFile(null);
        setLogoFontFile(null);
        if (anySaved) {
          toast.success('Logos saved locally (dev fallback).', { id: loadingId });
        } else {
          toast.error(`Failed to upload logos: ${e?.message || 'Unknown error'}`, { id: loadingId });
        }
      } catch {
        toast.error(`Failed to upload logos: ${e?.message || 'Unknown error'}`, { id: loadingId });
      }
    } finally {
      setSavingLogos(false);
    }
  };

  useEffect(() => {
    loadSecret();
  }, []);

  const saveBrandingIdentity = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }

    setSavingIdentity(true);
    const loadingId = toast.loading('Saving branding identity...');
    try {
      const payload: any = {
        mode,
        branding: {
          platformName: (platformName || '').trim(),
          mainAuthorName: (mainAuthorName || '').trim(),
          supportEmail: (supportEmail || '').trim(),
          appUrl: (appUrl || '').trim(),
        },
        token,
      };
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) { const detail = json?.error || raw || 'No response body'; throw new Error(`${res.status} ${res.statusText} - ${detail}`); }
      toast.success('Branding identity saved', { id: loadingId });
    } catch (e: any) {
      toast.error(`Failed to save branding identity: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setSavingIdentity(false);
    }
  };

  // Load current website-settings.json to display logo paths
  useEffect(() => {
    const loadWebsiteSettings = async () => {
      try {
        const res = await fetch('/website-settings.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (typeof data?.logoPath === 'string') setLogoPath(data.logoPath);
        if (typeof data?.logoFontPath === 'string') setLogoFontPath(data.logoFontPath);
        if (Array.isArray(data?.homepage?.gallery)) setGallery(data.homepage.gallery.filter((p: any) => typeof p === 'string'));
        if (Array.isArray(data?.keywords)) setKeywords(data.keywords.filter((k: any) => typeof k === 'string'));
        if (data?.productOptions && typeof data.productOptions === 'object') setProductOptions((prev) => ({ ...prev, ...data.productOptions }));
        if (typeof data?.mode === 'string' && ['shop','job','med','edu'].includes(data.mode)) {
          setMode(data.mode);
        }
        if (typeof data?.uploadTarget === 'string' && ['supabase','backblaze'].includes(data.uploadTarget)) {
          setUploadTarget(data.uploadTarget);
        }
        if (data?.branding && typeof data.branding === 'object') {
          const br = data.branding as any;
          if (typeof br.platformName === 'string') setPlatformName(br.platformName);
          if (typeof br.mainAuthorName === 'string') setMainAuthorName(br.mainAuthorName);
          if (typeof br.supportEmail === 'string') setSupportEmail(br.supportEmail);
          if (typeof br.appUrl === 'string') setAppUrl(br.appUrl);
        }
        if (typeof data?.colorScheme === 'string') {
          setColorScheme(getColorSchemeById(data.colorScheme).id);
        }
        if (data?.backblaze && typeof data.backblaze === 'object') {
          setBackblaze((prev) => ({
            enabled: typeof data.backblaze.enabled === 'boolean' ? data.backblaze.enabled : prev.enabled,
            bucket: typeof data.backblaze.bucket === 'string' ? data.backblaze.bucket : prev.bucket,
            endpoint: typeof data.backblaze.endpoint === 'string' ? data.backblaze.endpoint : prev.endpoint,
            folderPrefix: typeof data.backblaze.folderPrefix === 'string' ? data.backblaze.folderPrefix : prev.folderPrefix,
          }));
        }
        // Load persisted layout so the UI reflects saved selection before any save
        if (data?.layout && typeof data.layout === 'object') {
          const ly = data.layout as any;
          if (typeof ly.container === 'string') setLayoutContainer(ly.container);
          if (typeof ly.contentPaddingX === 'number') setLayoutPaddingX(ly.contentPaddingX);
          if (typeof ly.paddingMode === 'string') setLayoutPaddingMode(ly.paddingMode === 'custom' ? 'custom' : 'preset');
          if (typeof ly.paddingPreset === 'number') setLayoutPaddingPreset(ly.paddingPreset);
        }
      } catch {}
      // Also load local dev fallback for preview if no paths from server
      try {
        if (!logoPath) {
          const lp = window.localStorage.getItem('branding.logoDataUrl');
          if (lp) setLogoPath(lp);
        }
        if (!logoFontPath) {
          const lpf = window.localStorage.getItem('branding.logoFontDataUrl');
          if (lpf) setLogoFontPath(lpf);
        }
      } catch {}
    };
    loadWebsiteSettings();
    // Load Backblaze config directly from Supabase (client-side)
    const loadB2 = async () => {
      try {
        const { data, error } = await supabase
          .from('backblaze_config')
          .select('id, active, bucket, endpoint, folder_prefix, key_id, application_key')
          .eq('active', true)
          .maybeSingle();
        if (error) return;
        if (data) {
          setBackblaze((prev) => ({
            enabled: typeof data.active === 'boolean' ? data.active : prev.enabled,
            bucket: typeof data.bucket === 'string' ? data.bucket : prev.bucket,
            endpoint: typeof data.endpoint === 'string' ? data.endpoint : prev.endpoint,
            folderPrefix: typeof data.folder_prefix === 'string' ? data.folder_prefix : prev.folderPrefix,
          }));
          setB2SecretsPresent(!!(data.key_id && data.application_key));
          setB2RowId(typeof data.id === 'string' ? data.id : null);
          if (typeof data.key_id === 'string') setB2KeyId(data.key_id);
          if (typeof data.application_key === 'string') setB2ApplicationKey(data.application_key);
        }
      } catch {}
    };
    loadB2();
  }, []);

  // Open gallery picker for a target (main logo or wordmark)
  const openGalleryFor = (target: 'logo' | 'logoFont') => {
    setGalleryPicker({ open: true, target, selected: null });
  };
  const closeGalleryPicker = () => setGalleryPicker({ open: false, target: null, selected: null });
  const applyFromGallery = async () => {
    if (!galleryPicker.selected || !galleryPicker.target) return;
    if (galleryPicker.target === 'logo') setLogoPath(galleryPicker.selected);
    else setLogoFontPath(galleryPicker.selected);
    closeGalleryPicker();
    await saveLogos();
  };

  const saveSettings = async (nextMode: 'shop' | 'job' | 'med' | 'edu') => {
    const name = modes.find(m => m.id === nextMode)?.label || 'Mode';
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    // Ensure we have the latest secret
    let token = secret;
    if (!token) {
      const s = await AdminKeyService.getSecret();
      setSecret(s);
      token = s;
    }
    if (!token) {
      toast.error('Missing admin key. Please try again or contact support.');
      return;
    }
    console.log('[WebsiteSettings] Saving website settings', { mode: nextMode, endpoint });
    const loadingToastId = toast.loading('Saving website settings...');
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ mode: nextMode, token }),
      });
      const raw = await res.text();
      let json: any = null;
      try { json = raw ? JSON.parse(raw) : null; } catch { /* fallthrough */ }
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        const msg = `${res.status} ${res.statusText} - ${detail}`;
        // Development hint when running npm start (CRA dev server can't run PHP)
        const hint = res.status === 404 ? ' Endpoint likely not available in development (npm start). Deploy to Hostinger or run a local PHP server for testing.' : '';
        console.error('Website mode save failed:', { status: res.status, statusText: res.statusText, body: raw });
        throw new Error(msg + hint);
      }
      setMode(nextMode);
      toast.success(`${name} settings saved`, { id: loadingToastId });
      console.log('[WebsiteSettings] Website settings saved successfully', { mode: nextMode });
    } catch (e: any) {
      // Ignore aborted fetch errors that can happen during navigation or reload
      if (e?.name === 'AbortError') {
        console.warn('[WebsiteSettings] Request aborted (likely due to navigation/reload)', e);
        toast.dismiss(loadingToastId);
        return;
      }
      console.error('[WebsiteSettings] Error while saving website settings', e);
      toast.error(`Failed to save setting: ${e?.message || 'Unknown error'}` , { id: loadingToastId });
    }
  };

  const selectMode = async (id: (typeof modes)[number]['id']) => {
    if (id === mode) return;
    const picked = modes.find((m) => m.id === id);
    if ((picked as any)?.comingSoon) {
      toast('Coming soon ✨', { icon: '⏳' } as any);
      return;
    }
    if (!['shop', 'job', 'med', 'edu'].includes(id)) {
      toast.error('This mode is not available yet.');
      return;
    }
    await saveSettings(id as any);
  };

  const saveBackblaze = async () => {
    const loadingId = toast.loading('Saving Backblaze settings...');
    setSavingBackblaze(true);
    try {
      // Ensure we have admin token
      let token = secret;
      if (!token) {
        const s = await AdminKeyService.getSecret();
        setSecret(s);
        token = s;
      }
      if (!token) {
        throw new Error('Missing admin key');
      }

      const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
      const payload: any = {
        mode,
        backblaze: {
          enabled: backblaze.enabled,
          bucket: backblaze.bucket,
          endpoint: backblaze.endpoint,
          folderPrefix: backblaze.folderPrefix,
          keyId: (b2KeyId || '').trim() || undefined,
          applicationKey: (b2ApplicationKey || '').trim() || undefined,
        },
        token,
      };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let json: any = null;
      try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }

      // Reflect saved state
      if (json?.backblaze) {
        const bb = json.backblaze as any;
        setBackblaze((prev) => ({
          enabled: typeof bb.enabled === 'boolean' ? bb.enabled : prev.enabled,
          bucket: typeof bb.bucket === 'string' ? bb.bucket : prev.bucket,
          endpoint: typeof bb.endpoint === 'string' ? bb.endpoint : prev.endpoint,
          folderPrefix: typeof bb.folderPrefix === 'string' ? bb.folderPrefix : prev.folderPrefix,
        }));
      }
      if ((b2KeyId && b2ApplicationKey) || (payload.backblaze.keyId && payload.backblaze.applicationKey)) {
        setB2SecretsPresent(true);
      }
      toast.success('Backblaze settings saved', { id: loadingId });
    } catch (e: any) {
      toast.error(`Failed to save Backblaze settings: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setSavingBackblaze(false);
    }
  };

  const testUploadToBackblaze = async () => {
    if (!b2TestFile) return;
    const loadingId = toast.loading('Uploading test file to Backblaze...');
    try {
      // Ensure admin token for server-side upload endpoint
      let token = secret;
      if (!token) {
        const s = await AdminKeyService.getSecret();
        setSecret(s);
        token = s;
      }
      if (!token) throw new Error('Missing admin key');
      // Upload via PHP to avoid browser CORS
      const fd = new FormData();
      fd.append('file', b2TestFile);
      fd.append('bucket', (backblaze.bucket || '').trim());
      fd.append('endpoint', (backblaze.endpoint || '').trim());
      fd.append('folderPrefix', (backblaze.folderPrefix || '').trim());
      fd.append('keyId', (b2KeyId || '').trim());
      fd.append('applicationKey', (b2ApplicationKey || '').trim());
      const res = await fetch('/api/b2-upload.php', {
        method: 'POST',
        headers: { 'X-Auth-Token': token as string, 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        const hint = res.status === 404 ? ' Endpoint likely not available in local development. Deploy to your hosting or run a local PHP with OpenSSL enabled.' : '';
        throw new Error(`${res.status} ${res.statusText} - ${detail}${hint}`);
      }
      toast.success(`Uploaded: ${json.publicUrl || json.objectKey}`, { id: loadingId });
      setB2TestFile(null);
    } catch (e: any) {
      toast.error(`Upload failed: ${e?.message || 'Unknown error'}`, { id: loadingId });
    }
  };

  return (
    <Box>
      {/* Layout Settings */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 12px 32px rgba(2,6,23,0.10)' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Layout</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Steuert die maximale Seitenbreite und den Abstand zum linken/rechten Seitenrand.
            Gilt für Startseiten-Bereiche und die zweite Navigationsleiste.
          </Typography>
          <Tabs
            value={layoutEditTab === 'width' ? 0 : 1}
            onChange={(_, v) => setLayoutEditTab(v === 0 ? 'width' : 'padding')}
            sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36 } }}
          >
            <Tab label="Seitenbreite" />
            <Tab label="Abstand" />
          </Tabs>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Select
              value={layoutContainer}
              onChange={(e) => setLayoutContainer(String(e.target.value))}
              displayEmpty
              disabled={layoutEditTab !== 'width'}
              sx={{ opacity: layoutEditTab === 'width' ? 1 : 0.5 }}
            >
              <MenuItem value={'false'}>Vollbreite (keine feste Seitenbreite)</MenuItem>
              <MenuItem value={'sm'}>Seitenbreite: Klein (sm)</MenuItem>
              <MenuItem value={'md'}>Seitenbreite: Mittel (md)</MenuItem>
              <MenuItem value={'lg'}>Seitenbreite: Groß (lg)</MenuItem>
              <MenuItem value={'xl'}>Seitenbreite: Sehr groß (xl)</MenuItem>
            </Select>
            <Box sx={{ pointerEvents: layoutEditTab === 'padding' ? 'auto' : 'none', opacity: layoutEditTab === 'padding' ? 1 : 0.5 }}>
              <Tabs
                value={layoutPaddingMode === 'preset' ? 0 : 1}
                onChange={(_, v) => setLayoutPaddingMode(v === 0 ? 'preset' : 'custom')}
                sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36 } }}
              >
                <Tab label="Preset" />
                <Tab label="Custom" />
              </Tabs>

              <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
                <Select
                  value={String(layoutPaddingPreset)}
                  onChange={(e) => setLayoutPaddingPreset(Number(e.target.value))}
                  fullWidth
                  disabled={layoutEditTab !== 'padding' || layoutPaddingMode !== 'preset'}
                  sx={{ opacity: layoutEditTab === 'padding' && layoutPaddingMode === 'preset' ? 1 : 0.5 }}
                >
                  <MenuItem value={'0'}>0 px (kein zusätzlicher Rand)</MenuItem>
                  <MenuItem value={'8'}>8 px</MenuItem>
                  <MenuItem value={'12'}>12 px</MenuItem>
                  <MenuItem value={'16'}>16 px</MenuItem>
                  <MenuItem value={'20'}>20 px</MenuItem>
                  <MenuItem value={'24'}>24 px</MenuItem>
                  <MenuItem value={'32'}>32 px</MenuItem>
                  <MenuItem value={'40'}>40 px</MenuItem>
                  <MenuItem value={'48'}>48 px</MenuItem>
                </Select>

                <TextField
                  type="number"
                  label="Seitenrand‑Abstand (px)"
                  helperText="Empfehlung: 0–48 px. Werte über 96 können unpraktisch wirken."
                  value={layoutPaddingX}
                  onChange={(e) => setLayoutPaddingX(Number(e.target.value))}
                  inputProps={{ min: 0, max: 200 }}
                  fullWidth
                  disabled={layoutEditTab !== 'padding' || layoutPaddingMode !== 'custom'}
                  sx={{ opacity: layoutEditTab === 'padding' && layoutPaddingMode === 'custom' ? 1 : 0.5 }}
                />
              </Box>
            </Box>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button variant="contained" disabled={savingLayout} onClick={saveLayout}>Save Layout</Button>
          </Box>
        </CardContent>
      </Card>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Website Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Choose how your platform behaves. Changes are saved instantly.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <Chip
            icon={<CheckCircleIcon />}
            color="info"
            label={`Current: ${modes.find(m => m.id === mode)?.label}`}
            sx={{ fontWeight: 600 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => saveSettings(mode)}
            sx={{ textTransform: 'none' }}
          >
            Save Mode Settings
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
        }}
      >
        {modes.map((m) => {
          const active = m.id === mode;
          const comingSoon = Boolean((m as any).comingSoon);
          return (
            <Box key={m.id}>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Card
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!comingSoon) selectMode(m.id);
                  }}
                  sx={{
                    cursor: comingSoon ? 'not-allowed' : 'pointer',
                    border: comingSoon
                      ? '2px solid rgba(148, 163, 184, 0.35)'
                      : `2px solid ${active ? theme.palette[m.color].main : 'transparent'}`,
                    bgcolor: comingSoon
                      ? 'linear-gradient(135deg, rgba(248,250,252,1) 0%, rgba(241,245,249,1) 100%)'
                      : active
                        ? (theme.palette[m.color].light + '20')
                        : 'background.paper',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      borderColor: comingSoon ? 'rgba(148, 163, 184, 0.55)' : theme.palette[m.color].main,
                      bgcolor: comingSoon ? 'rgba(248,250,252,1)' : theme.palette[m.color].light + '10',
                    },
                    opacity: comingSoon ? 0.92 : 1,
                    position: 'relative',
                  }}
                >
                  <CardContent>
                    {comingSoon && (
                      <Chip
                        label="Coming soon"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          fontWeight: 800,
                          bgcolor: 'rgba(15,23,42,0.06)',
                          color: 'rgba(15,23,42,0.85)',
                          border: '1px solid rgba(148, 163, 184, 0.6)',
                        }}
                      />
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Avatar sx={{ bgcolor: comingSoon ? 'grey.200' : (active ? theme.palette[m.color].main : 'grey.300'), width: 64, height: 64, mb: 2, color: comingSoon ? 'grey.700' : 'inherit' }}>
                        {m.icon}
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {m.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {m.description}
                      </Typography>
                      <Button
                        variant={active ? 'contained' : 'outlined'}
                        color={m.color as any}
                        disabled={comingSoon}
                        onClick={(e) => { e.stopPropagation(); if (!comingSoon) selectMode(m.id); }}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {comingSoon ? 'Coming Soon' : (active ? 'Active' : 'Activate')}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Box>
          );
        })}
      </Box>

      {/* Logo Uploads */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Branding
        </Typography>
        <Card>
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Main Logo</Typography>
                {logoPath && (
                  <Box sx={{ mb: 1 }}>
                    <img src={toPublicUrl(logoPath)} alt="Current Logo" style={{ maxHeight: 48 }} />
                    <Typography variant="caption" color="text.secondary" display="block">{logoPath.startsWith('data:') ? 'Custom image (preview)' : logoPath.replace(/^.*[\\/]/, '')}</Typography>
                  </Box>
                )}
                <Button variant="outlined" component="label" sx={{ textTransform: 'none', mr: 1 }}>
                  {logoFile ? `Selected: ${logoFile.name}` : 'Choose File'}
                  <input type="file" hidden accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
                </Button>
                <Button variant="outlined" color="secondary" disabled={gallery.length === 0} onClick={() => openGalleryFor('logo')} sx={{ textTransform: 'none' }}>
                  Aus Galerie wählen
                </Button>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Font/Wordmark Logo</Typography>
                {logoFontPath && (
                  <Box sx={{ mb: 1 }}>
                    <img src={toPublicUrl(logoFontPath)} alt="Current Font Logo" style={{ maxHeight: 48 }} />
                    <Typography variant="caption" color="text.secondary" display="block">{logoFontPath.startsWith('data:') ? 'Custom image (preview)' : logoFontPath.replace(/^.*[\\/]/, '')}</Typography>
                  </Box>
                )}
                <Button variant="outlined" component="label" sx={{ textTransform: 'none', mr: 1 }}>
                  {logoFontFile ? `Selected: ${logoFontFile.name}` : 'Choose File'}
                  <input type="file" hidden accept="image/*" onChange={(e) => setLogoFontFile(e.target.files?.[0] || null)} />
                </Button>
                <Button variant="outlined" color="secondary" disabled={gallery.length === 0} onClick={() => openGalleryFor('logoFont')} sx={{ textTransform: 'none' }}>
                  Aus Galerie wählen
                </Button>
              </Box>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Button variant="contained" onClick={saveLogos} disabled={savingLogos || (!logoFile && !logoFontFile)} sx={{ textTransform: 'none' }}>
                {savingLogos ? 'Saving...' : 'Save Logos'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ mt: 2 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Color Scheme</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a palette for the entire website. This updates buttons, accents and the page glow.
                  </Typography>
                </Box>
                <Button variant="contained" onClick={saveColorScheme} disabled={savingColorScheme} sx={{ textTransform: 'none' }}>
                  {savingColorScheme ? 'Saving...' : 'Save Scheme'}
                </Button>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 2,
                }}
              >
                {colorSchemes.map((s) => {
                  const active = s.id === colorScheme;
                  return (
                    <motion.div key={s.id} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                      <Card
                        onClick={() => {
                          setColorScheme(s.id);
                          try {
                            window.dispatchEvent(new CustomEvent('websiteSettingsUpdated', { detail: { colorScheme: s.id } }));
                          } catch {
                            // ignore
                          }
                        }}
                        sx={{
                          cursor: 'pointer',
                          borderRadius: 3,
                          border: active ? `2px solid ${s.primary}` : '1px solid',
                          borderColor: active ? s.primary : 'divider',
                          overflow: 'hidden',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Box sx={{ height: 56, background: s.gradient }} />
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                            <Box>
                              <Typography sx={{ fontWeight: 900 }}>{s.name}</Typography>
                              <Typography variant="body2" color="text.secondary">{s.description}</Typography>
                            </Box>
                            {active && (
                              <Chip
                                size="small"
                                icon={<CheckCircleIcon />}
                                label="Active"
                                sx={{ fontWeight: 800 }}
                              />
                            )}
                          </Box>

                          <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center' }}>
                            <Box sx={{ width: 18, height: 18, borderRadius: 999, bgcolor: s.primary, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)' }} />
                            <Box sx={{ width: 18, height: 18, borderRadius: 999, bgcolor: s.secondary, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)' }} />
                            <Box sx={{ flex: 1 }} />
                            <Button
                              size="small"
                              variant={active ? 'contained' : 'outlined'}
                              sx={{ textTransform: 'none' }}
                            >
                              Preview
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Identity</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Used for labels across the app (e.g. notifications, headers, and future mail routing).
                  </Typography>
                </Box>
                <Button variant="contained" onClick={saveBrandingIdentity} disabled={savingIdentity} sx={{ textTransform: 'none' }}>
                  {savingIdentity ? 'Saving...' : 'Save Identity'}
                </Button>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  fullWidth
                  label="Platform / App name"
                  value={platformName}
                  onChange={(e) => setPlatformName(e.target.value)}
                  InputProps={{ startAdornment: <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}><AppsIcon fontSize="small" /></Box> }}
                />
                <TextField
                  fullWidth
                  label="Main author name"
                  value={mainAuthorName}
                  onChange={(e) => setMainAuthorName(e.target.value)}
                  InputProps={{ startAdornment: <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}><PersonIcon fontSize="small" /></Box> }}
                />
                <TextField
                  fullWidth
                  label="Support email (optional)"
                  value={supportEmail}
                  onChange={(e) => setSupportEmail(e.target.value)}
                  InputProps={{ startAdornment: <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}><AlternateEmailIcon fontSize="small" /></Box> }}
                />
                <TextField
                  fullWidth
                  label="App URL (optional)"
                  value={appUrl}
                  onChange={(e) => setAppUrl(e.target.value)}
                  InputProps={{ startAdornment: <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}><LinkIcon fontSize="small" /></Box> }}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <BackblazeHowToDialog open={howToOpen} onClose={() => setHowToOpen(false)} />
        {/* Gallery Picker for Branding */}
        <Dialog open={galleryPicker.open} onClose={closeGalleryPicker} maxWidth="md" fullWidth>
          <DialogTitle>Aus Galerie wählen</DialogTitle>
          <DialogContent dividers>
            {gallery.length === 0 ? (
              <Typography color="text.secondary">Keine Galerie-Bilder vorhanden.</Typography>
            ) : (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(6, 1fr)' },
                gap: 1.5,
              }}>
                {gallery.map((p) => {
                  const selected = galleryPicker.selected === p;
                  return (
                    <Box
                      key={p}
                      onClick={() => setGalleryPicker(prev => ({ ...prev, selected: p }))}
                      sx={{
                        position: 'relative',
                        borderRadius: 2,
                        border: selected ? '2px solid #7c3aed' : '1px solid rgba(0,0,0,0.12)',
                        overflow: 'hidden',
                        cursor: 'pointer',
                      }}
                    >
                      <img src={toPublicUrl(p)} alt={p} style={{ width: '100%', height: 120, objectFit: 'cover' }} />
                    </Box>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeGalleryPicker}>Abbrechen</Button>
            <Button variant="contained" onClick={applyFromGallery} disabled={!galleryPicker.selected}>Übernehmen</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={b2Preview.open} onClose={() => setB2Preview({ open: false, url: null, name: null })} maxWidth="md" fullWidth>
          <DialogTitle>{b2Preview.name?.split('/').pop() || 'Preview'}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {b2Preview.url ? (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <img src={b2Preview.url} alt={b2Preview.name || ''} style={{ maxWidth: '100%', maxHeight: 480, objectFit: 'contain' }} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No preview available</Typography>
              )}
              <TextField
                label="URL"
                fullWidth
                value={b2Preview.url || ''}
                InputProps={{ readOnly: true }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { if (b2Preview.url) window.open(b2Preview.url, '_blank'); }} disabled={!b2Preview.url} sx={{ textTransform: 'none' }}>Open</Button>
            <Button onClick={async () => { if (b2Preview.url) { try { await navigator.clipboard.writeText(b2Preview.url); toast.success('URL copied'); } catch { /* ignore */ } } }} disabled={!b2Preview.url} sx={{ textTransform: 'none' }}>Copy URL</Button>
            <Button variant="contained" onClick={() => setB2Preview({ open: false, url: null, name: null })} sx={{ textTransform: 'none' }}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>

      {isShopMode && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight="bold">
              Product Types Allowed
            </Typography>
            <Chip
              icon={<ShoppingCartIcon />}
              label="Shop mode"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
          </Box>
          <Card>
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 700 }}>Festpreis</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  {[
                    { key: 'fixed_single', title: 'Festpreis — Einzelverkauf', desc: 'Direktkauf eines Artikels', icon: <FixedIcon />, color: 'primary' },
                    { key: 'fixed_multiple', title: 'Festpreis — Mehrere', desc: 'Direktkauf mit Bestand', icon: <MultipleIcon />, color: 'info' },
                  ].map((o) => {
                    const active = !!productOptions[o.key as keyof typeof productOptions];
                    return (
                      <motion.div key={o.key} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Card
                          variant={active ? 'elevation' : 'outlined'}
                          sx={{ height: '100%', borderWidth: active ? 2 : 1, borderColor: active ? `${o.color}.main` : 'divider', bgcolor: active ? `${o.color}.50` : 'background.paper', transition: 'all 0.2s ease' }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Avatar sx={{ bgcolor: `${o.color}.main`, width: 36, height: 36 }}>{o.icon}</Avatar>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{o.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{o.desc}</Typography>
                                </Box>
                              </Box>
                              <Switch checked={active} onChange={(e) => setProductOptions((p) => ({ ...p, [o.key]: e.target.checked }))} />
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 700 }}>Verhandlungsbasis</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  {[
                    { key: 'negotiable_single', title: 'Verhandlungsbasis — Einzel', desc: 'Kauf mit Angebot (1 Artikel)', icon: <NegotiableIcon />, color: 'secondary' },
                    { key: 'negotiable_multiple', title: 'Verhandlungsbasis — Mehrere', desc: 'Kauf mit Angebot (Bestand)', icon: <NegotiableIcon />, color: 'warning' },
                  ].map((o) => {
                    const active = !!productOptions[o.key as keyof typeof productOptions];
                    return (
                      <motion.div key={o.key} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Card
                          variant={active ? 'elevation' : 'outlined'}
                          sx={{ height: '100%', borderWidth: active ? 2 : 1, borderColor: active ? `${o.color}.main` : 'divider', bgcolor: active ? `${o.color}.50` : 'background.paper', transition: 'all 0.2s ease' }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Avatar sx={{ bgcolor: `${o.color}.main`, width: 36, height: 36 }}>{o.icon}</Avatar>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{o.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{o.desc}</Typography>
                                </Box>
                              </Box>
                              <Switch checked={active} onChange={(e) => setProductOptions((p) => ({ ...p, [o.key]: e.target.checked }))} />
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 700 }}>Weitere</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  {[
                    { key: 'auction', title: 'Auktion (Bieten)', desc: 'Biet-Verfahren', icon: <AuctionIcon />, color: 'error' },
                    { key: 'promotion', title: 'Angebot / Aktion', desc: 'Promotionen aktivieren', icon: <PromotionIcon />, color: 'success' },
                  ].map((o) => {
                    const active = !!productOptions[o.key as keyof typeof productOptions];
                    return (
                      <motion.div key={o.key} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Card
                          variant={active ? 'elevation' : 'outlined'}
                          sx={{ height: '100%', borderWidth: active ? 2 : 1, borderColor: active ? `${o.color}.main` : 'divider', bgcolor: active ? `${o.color}.50` : 'background.paper', transition: 'all 0.2s ease' }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Avatar sx={{ bgcolor: `${o.color}.main`, width: 36, height: 36 }}>{o.icon}</Avatar>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{o.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{o.desc}</Typography>
                                </Box>
                              </Box>
                              <Switch checked={active} onChange={(e) => setProductOptions((p) => ({ ...p, [o.key]: e.target.checked }))} />
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </Box>
              </Box>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={saveProductOptions} sx={{ textTransform: 'none' }}>
                  Save Product Types
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Backblaze Cloud Storage */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Backblaze Cloud Storage
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {b2SecretsPresent !== null && (
              <Chip size="small" color={b2SecretsPresent ? 'success' : 'default'} label={b2SecretsPresent ? 'Credentials: Saved' : 'Credentials: Not set'} />
            )}
            <Button variant="contained" color="secondary" onClick={() => setHowToOpen(true)} sx={{ textTransform: 'none', borderRadius: 999 }}>
              How to connect
            </Button>
          </Box>
        </Box>
        <Card>
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Box>
                <FormControlLabel
                  control={<Switch checked={backblaze.enabled} onChange={(e) => setBackblaze((p) => ({ ...p, enabled: e.target.checked }))} />}
                  label="Enable Backblaze (uses S3-compatible presigned uploads)"
                />
                <TextField
                  fullWidth
                  label="Bucket"
                  value={backblaze.bucket}
                  onChange={(e) => setBackblaze((p) => ({ ...p, bucket: e.target.value }))}
                  sx={{ mt: 2 }}
                />
                <TextField
                  fullWidth
                  label="Endpoint (e.g. https://s3.eu-central-003.backblazeb2.com)"
                  value={backblaze.endpoint}
                  onChange={(e) => setBackblaze((p) => ({ ...p, endpoint: e.target.value }))}
                  sx={{ mt: 2 }}
                />
                <TextField
                  fullWidth
                  label="Folder Prefix"
                  value={backblaze.folderPrefix}
                  onChange={(e) => setBackblaze((p) => ({ ...p, folderPrefix: e.target.value }))}
                  sx={{ mt: 2 }}
                />
                <TextField
                  fullWidth
                  label="Key ID"
                  value={b2KeyId}
                  onChange={(e) => setB2KeyId(e.target.value)}
                  sx={{ mt: 2 }}
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Application Key"
                  value={b2ApplicationKey}
                  onChange={(e) => setB2ApplicationKey(e.target.value)}
                  sx={{ mt: 2 }}
                />
              </Box>
              
            </Box>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Upload Target:</Typography>
                <Button
                  size="small"
                  variant={uploadTarget === 'supabase' ? 'contained' : 'outlined'}
                  onClick={() => saveUploadTarget('supabase')}
                  disabled={savingUploadTarget}
                  sx={{ textTransform: 'none' }}
                >Supabase</Button>
                <Button
                  size="small"
                  variant={uploadTarget === 'backblaze' ? 'contained' : 'outlined'}
                  onClick={() => saveUploadTarget('backblaze')}
                  disabled={savingUploadTarget}
                  sx={{ textTransform: 'none' }}
                >Backblaze</Button>
              </Box>
              <Button variant="contained" onClick={saveBackblaze} disabled={savingBackblaze} sx={{ textTransform: 'none' }}>
                {savingBackblaze ? 'Saving...' : 'Save Backblaze Settings'}
              </Button>
              <Button variant="outlined" onClick={testB2Connection} disabled={!((b2KeyId||'').trim() && (b2ApplicationKey||'').trim())} sx={{ textTransform: 'none' }}>
                Test Connection
              </Button>
              <Button variant="outlined" component="label" disabled={!backblaze.enabled} sx={{ textTransform: 'none' }}>
                {b2TestFile ? `Selected: ${b2TestFile.name}` : 'Choose Test File'}
                <input type="file" hidden onChange={(e) => setB2TestFile(e.target.files?.[0] || null)} />
              </Button>
              <Button variant="outlined" onClick={testUploadToBackblaze} disabled={!b2TestFile || !backblaze.enabled} sx={{ textTransform: 'none' }}>
                Test Upload
              </Button>
              <Button variant="outlined" onClick={() => fetchBackblazeList('reset')} disabled={!backblaze.enabled} sx={{ textTransform: 'none' }}>
                Fetch Files
              </Button>
            </Box>

            {b2Items.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 700 }}>Files in {backblaze.folderPrefix || '/'} ({b2Items.length}{b2NextToken ? '+' : ''})</Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                  gap: 2,
                }}>
                  {b2Items.map((it) => (
                    <Card key={it.key} variant="outlined" sx={{ overflow: 'hidden', position: 'relative' }}>
                      <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, display: 'flex', gap: 0.5 }}>
                        <Button size="small" variant="contained" color="error" onClick={async (e) => {
                          e.stopPropagation();
                          const confirmDel = window.confirm(`Delete ${it.key.split('/').pop()}?`);
                          if (!confirmDel) return;
                          const toastId = toast.loading('Deleting...');
                          try {
                            let token = secret;
                            if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
                            if (!token) throw new Error('Missing admin key');
                            const payload = {
                              bucket: (backblaze.bucket || '').trim(),
                              endpoint: (backblaze.endpoint || '').trim(),
                              key: it.key,
                              keyId: (b2KeyId || '').trim(),
                              applicationKey: (b2ApplicationKey || '').trim(),
                            };
                            const res = await fetch('/api/b2-delete.php', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
                              body: JSON.stringify(payload),
                            });
                            const raw = await res.text();
                            let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
                            if (!res.ok || !json?.ok) {
                              const detail = json?.error || raw || 'No response body';
                              throw new Error(`${res.status} ${res.statusText} - ${detail}`);
                            }
                            setB2Items((prev) => prev.filter(x => x.key !== it.key));
                            toast.success('Deleted', { id: toastId });
                          } catch (err: any) {
                            toast.error(`Delete failed: ${err?.message || 'Unknown error'}`);
                          }
                        }} sx={{ minWidth: 0, px: 1, textTransform: 'none', lineHeight: 1 }}>×</Button>
                      </Box>
                      <Box sx={{ position: 'relative', pt: '56%', bgcolor: 'grey.100', cursor: 'pointer' }} onClick={() => setB2Preview({ open: true, url: it.url || undefined as any, name: it.key })}>
                        {it.url ? (
                          <img
                            src={it.url}
                            alt={it.key}
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', backgroundColor: '#eee' }}
                          />
                        ) : (
                          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', fontSize: 12 }}>No preview</Box>
                        )}
                      </Box>
                      <Box sx={{ p: 1.25 }}>
                        <Typography variant="body2" noWrap title={it.key} sx={{ fontWeight: 600 }}>{it.key.split('/').pop()}</Typography>
                        <Typography variant="caption" color="text.secondary">{(it.size/1024).toFixed(1)} KB · {new Date(it.lastModified).toLocaleString()}</Typography>
                      </Box>
                    </Card>
                  ))}
                </Box>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button variant="text" onClick={() => fetchBackblazeList('next')} disabled={!b2NextToken || b2LoadingList} sx={{ textTransform: 'none' }}>
                    {b2NextToken ? (b2LoadingList ? 'Loading…' : 'Load more') : 'All loaded'}
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
        <BackblazeHowToDialog open={howToOpen} onClose={() => setHowToOpen(false)} />
      </Box>

      {/* Keyword Filters */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Keyword Filters
        </Typography>
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Type keywords separated by commas. They’ll appear as labels.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                placeholder="e.g. scam, forbidden, politics"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onBlur={addKeywordsFromInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addKeywordsFromInput();
                  }
                }}
              />
              <Button variant="outlined" onClick={addKeywordsFromInput} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {keywords.map((k) => (
                <Chip key={k} label={k} onDelete={() => removeKeyword(k)} color="default" sx={{ borderRadius: 2 }} />
              ))}
              {keywords.length === 0 && (
                <Typography variant="body2" color="text.secondary">No keywords yet.</Typography>
              )}
            </Box>

            <Box sx={{ mt: 3 }}>
              <Button variant="contained" onClick={saveKeywords} sx={{ textTransform: 'none' }}>
                Save Keyword Filters
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default WebsiteSettingsTab;
